

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {
  LockOutlined,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { registerUser } from '@/stores/slice/authSlice';
import { useDispatch } from 'react-redux';
import InspectionBtn from '@/components/InspectionBtn';
import { useAuth } from '@/hooks/useAuth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import toast from 'react-hot-toast';
import Logo from "@/assets/inspections1.png"

const RegisterForm = ({ onToggle }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch, setValue,
    reset
  } = useForm({
    mode: 'onChange'
  });

  const [loader, setLoader] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const refCode = searchParams.get('ref'); // Gets "MRAKUZI160"
  const decodeId = (encoded) => atob(encoded);
 
  const dispatch = useDispatch()
  const param = useParams()
  const watchPassword = watch('password', '');
  const { role, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [hasError, setError] = useState("");
   const onSubmit = async (data) => {
    let browserFingerprint;
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      browserFingerprint = result.visitorId;
    } catch (fpError) {
      console.error('Fingerprint generation failed:', fpError);
    }

    try {
      const paylod = {
        email: data?.email,
        password: data?.password,
        company_name: data?.company_name,
        username: data?.username,
        fingerprint: browserFingerprint,
        refcode: refCode ? decodeId(refCode):""

      }
      const res = await dispatch(registerUser(paylod));
      if (res.error?.message === "Rejected") {
        toast.error(res?.payload);
        setError(res?.payload);
        setLoader(false);
      } else if (registerUser.fulfilled.match(res)) {
        toast.success("Registration Completed");
        setError("");
        setLoader(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.msg || "Login failed");
      setLoader(false);
    }
  };

  const passwordRules = {
    required: 'Password is required',
    minLength: {
      value: 6,
      message: 'Password must be at least 8 characters long'
    },

  };
   useEffect(() => {
    setValue("email", param?.email)
    if (isAuthenticated && role) {
 
      switch (role) {
        case "ADMIN":
          navigate("/admin/dashboard", { replace: true });
          break;
        case "USER":
          navigate("/user/dashboard", { replace: true });
          break;
        case "OWNER":
          navigate("/manager/dashboard", { replace: true });
          break;
        default:
          navigate("/unauthorized", { replace: true });
      }
    }
  }, [role, isAuthenticated, navigate]);


  const confirmPasswordRules = {
    required: 'Please confirm your password',
    validate: (value) =>
      value === watchPassword || 'Passwords do not match'
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit)(e);
  };

  const handleLoginClick = () => {
    navigate("/login")
  };

  return (
    <div className="min-h-screen flex items-center justify-center   bg-gradient-to-r from-[#0087af] to-emerald-400py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-10">
          <div className="flex justify-center items-center mb-6">
                                  <img
                                      src={Logo}
                                      alt="Company Logo"
                                      className="h-20 w-20 rounded object-contain"
                                  />
                                  <span className="text-[28px] font-extrabold tracking-tight bg-gradient-to-r from-[#0087af] to-[#00ae68] bg-clip-text text-transparent">
                                      SpotFlash
                                  </span>
          
                              </div>
          <div className="text-center mb-8">
            <h2 className="text-3xl flash-color font-bold text-gray-900">Create your account</h2>
            <p className="mt-2 text-sm text-gray-600">
              Join us today! Please fill in your information.
            </p>
          </div>
          {hasError && <p className='text-center text-red-500 mb-4'>{hasError}</p>}

          {/* GRID FORM */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                User Name
              </label>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="username"
                    type="text"
                    placeholder="Enter username"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
                  />
                )}
              />
              {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="email"
                    disabled={refCode ? false : true}
                    type="email"
                    placeholder="Enter email"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
                  />
                )}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>

            {/* Company Name */}
            <div className="md:col-span-2">
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <Controller
                name="company_name"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="company_name"
                    type="text"
                    placeholder="Enter company name"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
                  />
                )}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
                  />
                )}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
                  />
                )}
              />
            </div>

            {/* Ship Details - IMO + Name */}
            {/* <div>
          <label htmlFor="imo_number" className="block text-sm font-medium text-gray-700 mb-1">
            IMO Number (optional)
          </label>
          <Controller
            name="imo_number"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                id="imo_number"
                type="text"
                placeholder="IMO1234567"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
              />
            )}
          />
        </div>

        <div>
          <label htmlFor="ship_name" className="block text-sm font-medium text-gray-700 mb-1">
            Ship Name (optional)
          </label>
          <Controller
            name="ship_name"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                id="ship_name"
                type="text"
                placeholder="Enter ship name"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 border-gray-300"
              />
            )}
          />
        </div> */}
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <InspectionBtn
              onClick={handleFormSubmit}
              loading={loader}
              className="w-full   rounded-md shadow-md text-white bg-teal-600 hover:bg-teal-700 transition transform hover:scale-[1.02] focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Create Free Account
            </InspectionBtn>
          </div>

          {/* Switch to Sign in */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={handleLoginClick}
                className="font-medium text-teal-600 hover:text-teal-500 underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

  );
};

export default RegisterForm;